import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import QuestionGuidelines from './components/QuestionGuidelines';
import { observable } from 'legacy-packages/ft-cb';
import 'legacy/src/components/scoring/ScoringLayout.scss';
import './styles.scss';

const CLASSNAMES = {
  PLAYER_CONTAINER: 'cb-learnosity-player',
  CORRECT_ANSWERS: 'display-correct-answers',
  CONTAINER: 'PlayerRubricsPlaceHolder',
  IS_HIDDEN: 'is-hidden',
};

const SELECTORS = {
  QUESTION_CONTENT_VIEWPORT:
    '.lrn-assess-content.content.lrn > .row .lrn-viewport',
  QUESTION_ROW: '.lrn-assess-content.content.lrn > .row',
  RIGHT_COLUMN: '.right-column',
};

/**
 * Draws the player scoring rubrics
 * */
const PlayerGuidelines = (props) => {
  const {
    questions,
    isVisible,
    isPreApSubject,
    guidelinesHelp,
    shouldAutoScroll,
    playerContainerId,
    isBlueBook,
  } = props;
  const mainPlayerContainer = playerContainerId
    ? document.querySelector(`#${playerContainerId}`)
    : document;
  const {
    learnosityData: { app, items },
  } = props;
  const [item, setItem] = useState(app.getCurrentItem());

  useEffect(() => {
    /**
     * Render rubrics for a question
     * @param {object} item The learnosity question object
     * */
    if (!item) {
      return;
    }
    const { reference } = item;

    const questionElement = mainPlayerContainer.querySelector(
      `.learnosity-item[data-reference="${reference}"]`,
    );

    if (!questionElement) {
      return;
    }

    const placeHolder = questionElement.querySelector(
      `.${CLASSNAMES.CONTAINER}`,
    );

    let container;

    if (!isBlueBook && !placeHolder) {
      container = document.createElement('div');
      container.className = CLASSNAMES.CONTAINER;

      questionElement.appendChild(container);

      ReactDOM.render(
        <QuestionGuidelines
          app={app}
          isPreApSubject={isPreApSubject}
          question={item}
          questions={questions}
          guidelinesHelp={guidelinesHelp}
          shouldAutoScroll={shouldAutoScroll}
          playerContainer={mainPlayerContainer}
        />,
        container,
      );

      /**
       * Validate questions on item changing to allow learnosity draw correct answers
       * */
      app.validateQuestions({
        showCorrectAnswers: isVisible,
      });
    }

    if (!isBlueBook) return null;

    const rightColumn = questionElement.querySelector(SELECTORS.RIGHT_COLUMN);
    const containerPlaceHolder = questionElement?.querySelector(
      `.${CLASSNAMES.CONTAINER}`,
    );

    if (!containerPlaceHolder) {
      container = document.createElement('div');
      container.classList.add(CLASSNAMES.CONTAINER);
      const isMultiPartFrq = (item?.questions?.length || 0) > 1;
      const hasTwoNativeColumns =
        questionElement.querySelector('.col-xs-6') ||
        questionElement.querySelector('.col-sm-6');

      const isMultiPartWithNativeColumns =
        isMultiPartFrq && hasTwoNativeColumns;

      let bluebookDistractorContainer;
      if (isMultiPartWithNativeColumns) {
        bluebookDistractorContainer = questionElement.querySelector(
          SELECTORS.QUESTION_ROW,
        );
      } else {
        bluebookDistractorContainer =
          questionElement.querySelector(SELECTORS.QUESTION_CONTENT_VIEWPORT) ||
          questionElement.querySelector(SELECTORS.QUESTION_ROW);
      }

      if (rightColumn) {
        bluebookDistractorContainer
          ?.querySelector('.' + CLASSNAMES.CONTAINER)
          ?.remove();
        rightColumn.appendChild(container);
      } else {
        bluebookDistractorContainer?.appendChild(container);
      }

      ReactDOM.render(
        <QuestionGuidelines
          app={app}
          isPreApSubject={isPreApSubject}
          question={item}
          questions={questions}
          guidelinesHelp={guidelinesHelp}
          shouldAutoScroll={shouldAutoScroll}
          playerContainer={mainPlayerContainer}
        />,
        container,
      );

      /**
       * Validate questions on item changing to allow learnosity draw correct answers
       * */
      app.validateQuestions({
        showCorrectAnswers: isVisible,
      });
    }
  }, [isVisible, item]);

  useEffect(() => {
    if (!item) {
      return;
    }
    const { reference } = item;
    const questionElement = mainPlayerContainer.querySelector(
      `.learnosity-item[data-reference="${reference}"]`,
    );

    if (!questionElement) {
      return;
    }

    const playerContainer = mainPlayerContainer.querySelector(
      `.${CLASSNAMES.PLAYER_CONTAINER}`,
    );
    const placeHolder = questionElement.querySelector(
      `.${CLASSNAMES.CONTAINER}`,
    );

    if (isVisible) {
      placeHolder?.classList.remove(CLASSNAMES.IS_HIDDEN);
      playerContainer?.classList.add(CLASSNAMES.CORRECT_ANSWERS);
    } else {
      placeHolder?.classList.add(CLASSNAMES.IS_HIDDEN);
      playerContainer?.classList.remove(CLASSNAMES.CORRECT_ANSWERS);
    }
  }, [isVisible, item]);

  useEffect(() => {
    observable.emit('isVisible', isVisible);
  }, [isVisible]);

  useEffect(() => {
    const itemLoad = () => {
      setItem(app.getCurrentItem());
    };

    const itemChanging = (itemIndex) => {
      const reference = Object.keys(items)[itemIndex];
      observable.emit(`update-current-${reference}`, new Date().getTime());
    };

    const subscribePlayerUnmounts = () => {
      const { reference } = item;

      const questionElement = mainPlayerContainer.querySelector(
        `.learnosity-item[data-reference="${reference}"]`,
      );

      if (!questionElement) {
        return;
      }

      const placeHolder = questionElement.querySelector(
        `.${CLASSNAMES.CONTAINER}`,
      );
      if (placeHolder) {
        ReactDOM.unmountComponentAtNode(placeHolder);
      }
    };

    app.on('item:load', itemLoad);
    app.on('item:changing', itemChanging);
    observable.subscribe('preview-player-unmounted', subscribePlayerUnmounts);

    return () => {
      app.off('item:load', itemLoad);
      app.off('item:changing', itemChanging);
      observable.unsubscribe(
        `preview-player-unmounted`,
        subscribePlayerUnmounts,
      );
    };
  }, []);

  return null;
};

PlayerGuidelines.displayName = 'PlayerGuidelines';

/**
 * Need to refactor the MCQ and add it here
 * */
PlayerGuidelines.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  questions: PropTypes.array.isRequired,
  isPreApSubject: PropTypes.bool.isRequired,
  isBlueBook: PropTypes.bool,
};

PlayerGuidelines.defaultProps = {
  isBlueBook: false,
};

export default PlayerGuidelines;
